import React, { useMemo, useRef, useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import useFormatAmount from 'app-components/hooks/privacyMode/useFormatAmount';
import usePrivacyMaskValue from 'app-components/hooks/privacyMode/usePrivacyMaskValue';
import { buildLineChartOption } from 'utils/chart-options';
import type { LineChartConfig, ChartDataItem } from 'utils/chart-options';
import type { GeneratedReportRanges, Timestep } from 'app-components/utils/date';
import { makeStyles } from 'tss-react/mui';
import QButton from 'components/QButton';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import type { EChartsOption } from 'echarts';

export {
  STACKED_AREA_FILL_OPACITY,
  TOTAL_AREA_FILL_OPACITY,
  TOTAL_AREA_LINE_OPACITY,
} from 'utils/chart-options/line/constants';
import { getSinglePeriodRenderData } from './singlePeriodRenderData';
import EChartsBase, { type EChartsBaseHandle } from 'components/Charts/EChartsBase';
import useEChartsTheme from 'components/Charts/useEChartsTheme';
import useChartCursorDataY from 'components/Charts/useChartCursorDataY';
import { useHoveredStackedAreaField } from './useHoveredStackedAreaField';
import { useLegendSeriesTooltip } from './useLegendSeriesTooltip';

// 2 rows of 16px items + 1 row-gap of 8px + top/bottom padding (8px each)
const LEGEND_COLLAPSED_HEIGHT = 56;

// Module-level so the hook's handler identity is stable across renders.
const CURSOR_FINDER = { seriesIndex: 0 } as const;

const useStyles = makeStyles()((theme) => ({
  legendContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, 152px)',
    columnGap: 24,
    rowGap: 8,
    justifyContent: 'center',
    width: '100%',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    cursor: 'default',
  },
  legendMarker: {
    width: 16,
    height: 16,
    borderRadius: 2,
    flexShrink: 0,
  },
  legendLabel: {
    fontSize: theme.typography.bodyRegular.fontSize,
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
  },
  legendToggle: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    paddingTop: theme.spacing(0.5),
  },
}));

export interface MultilineChartRef {
  clearSelection: () => void;
  dispatchAction: (action: { type: string } & Record<string, unknown>) => void;
  highlightSeries: (field: string) => void;
  downplaySeries: (field: string) => void;
}

export interface MultilineChartProps {
  data: ChartDataItem[];
  height?: number;
  style?: React.CSSProperties;
  orderedFields?: string[];
  stacked?: boolean | boolean[];
  withLegend?: boolean;
  curvy?: boolean;
  hideAxes?: boolean;
  colorOverrides?: string[];
  areaOpacity?: number;
  lineOpacity?: number;
  lineWidth?: number;
  nearestSeriesTooltip?: boolean;
  useCustomLegend?: boolean;
  combineSeries?: boolean;
  combinedSeriesLabel?: string;
  yAxisZeroStart?: boolean;
  yAxisExtents?: { min: number; max: number };
  hideCents?: boolean;
  useNumberPrefix?: boolean;
  largeToSmall?: boolean;
  totalTooltipField?: string;
  // Append a summary row after a divider in the breakdown tooltip.
  showTooltipTotal?: boolean;
  // Label for the computed summary row when there is no totalTooltipField (e.g. Savings "Total").
  tooltipTotalLabel?: string;
  tooltipFields?: string[];
  seriesNames?: Record<string, string>;
  tooltipSeriesLabel?: LineChartConfig['tooltipSeriesLabel'];
  timestep?: string;
  // Selected report date range — drives the date-aware x-axis label strategy (SIMPL-34454).
  dateRange?: GeneratedReportRanges;
  preferMonthlyDateLabels?: boolean;
  // Pick the x-axis date labels from the visible day span alone, ignoring `timestep` — for data
  // that is one point per sub-monthly bucket (asset Market Value, SIMPL-34721).
  denseDateLabels?: boolean;
  // Percent value axis + tooltip and a y=0 baseline line (Investments TWR performancesChart).
  percent?: boolean;
  percentMaxMagnitude?: number;
  zeroBaselineLine?: boolean;
  clipHoverLineToArea?: boolean;
  stackedAreaLegendTooltipFields?: string[];
  // Object form shallow-merges over the built option; function form receives the
  // fully built option and returns a transformed one (for deep edits such as
  // adding markLine/markArea or overriding axis config).
  optionOverrides?: Record<string, unknown> | ((option: EChartsOption) => EChartsOption);
}

function MultilineChartImpl(
  {
    data,
    height = 400,
    style,
    orderedFields,
    stacked: stackedProp = false,
    withLegend = false,
    curvy = false,
    hideAxes = false,
    colorOverrides,
    areaOpacity,
    lineOpacity,
    lineWidth,
    nearestSeriesTooltip = false,
    useCustomLegend = false,
    combineSeries = false,
    combinedSeriesLabel,
    yAxisZeroStart = false,
    yAxisExtents,
    hideCents = false,
    useNumberPrefix = false,
    largeToSmall = false,
    totalTooltipField,
    showTooltipTotal = false,
    tooltipTotalLabel,
    tooltipFields,
    seriesNames,
    tooltipSeriesLabel,
    timestep,
    dateRange,
    preferMonthlyDateLabels = false,
    denseDateLabels = false,
    percent = false,
    percentMaxMagnitude,
    zeroBaselineLine = false,
    clipHoverLineToArea = true,
    stackedAreaLegendTooltipFields,
    optionOverrides,
  }: MultilineChartProps,
  ref: React.ForwardedRef<MultilineChartRef>,
): React.JSX.Element | null {
  const stacked = Array.isArray(stackedProp) ? stackedProp.some(Boolean) : stackedProp;
  const { classes } = useStyles();
  const echartsTheme = useEChartsTheme();
  const formatAmount = useFormatAmount();
  const privacyMask = usePrivacyMaskValue();

  const { chartData, chartFields } = useMemo(() => {
    if (!combineSeries || !data || !orderedFields) {
      return { chartData: data, chartFields: orderedFields ?? [] };
    }

    const seriesLabel = combinedSeriesLabel ?? 'Total Net Worth';
    const combinedData = data.map((dataPoint) => {
      let total = 0;
      orderedFields.forEach((field) => {
        const value = dataPoint[field];
        if (typeof value === 'number') {
          total += value;
        }
      });
      return {
        category: dataPoint.category,
        custom: dataPoint.custom,
        startOn: dataPoint.startOn,
        endOn: dataPoint.endOn,
        [seriesLabel]: total,
      } as ChartDataItem;
    });

    return { chartData: combinedData, chartFields: [seriesLabel] };
  }, [data, orderedFields, combineSeries, combinedSeriesLabel]);

  const usesStackedAreaInteractions = stacked || Boolean(stackedAreaLegendTooltipFields?.length);
  const shouldExpandSinglePeriod = usesStackedAreaInteractions || combineSeries;
  const renderData = useMemo(() => {
    const raw = getSinglePeriodRenderData(chartData ?? [], shouldExpandSinglePeriod, timestep ?? 'MONTH');
    if (raw.length > 1 && (chartData ?? []).length === 1) {
      return raw.map((pt, i) => (i === 0 ? pt : { ...pt, category: '' }));
    }
    return raw;
  }, [chartData, shouldExpandSinglePeriod, timestep]);

  const yAxisMin = useMemo(() => {
    if (stacked || !yAxisZeroStart) return undefined;
    // Mirror AMCharts: floor to the data minimum so large negatives aren't clipped.
    // If all values are >= 0 this returns 0 (axis starts at zero as expected).
    let min = 0;
    chartData?.forEach((dataPoint) => {
      chartFields.forEach((field) => {
        const val = dataPoint[field];
        if (typeof val === 'number' && val < min) min = val;
      });
    });
    return min;
  }, [stacked, yAxisZeroStart, chartData, chartFields]);

  const chartRef = useRef<EChartsBaseHandle>(null);

  const { legendHoveredFieldRef, showTooltipForField, hideTooltip } = useLegendSeriesTooltip(
    chartRef,
    chartData,
    chartFields,
    // Use the final plotted index; single-period charts expand from one source point to two render points.
    renderData.length - 1,
    usesStackedAreaInteractions ? stackedAreaLegendTooltipFields : undefined,
    totalTooltipField,
  );

  useImperativeHandle(
    ref,
    () => ({
      clearSelection() {},
      dispatchAction: (action) => chartRef.current?.dispatchAction(action),
      highlightSeries: (field: string) => {
        chartRef.current?.dispatchAction({ type: 'highlight', seriesName: field });
        showTooltipForField(field);
      },
      downplaySeries: (field: string) => {
        chartRef.current?.dispatchAction({ type: 'downplay', seriesName: field });
        hideTooltip();
      },
    }),
    [showTooltipForField, hideTooltip],
  );

  const {
    hoveredFieldNameRef,
    onMouseMoveCapture,
    onMouseLeave: onChartMouseLeave,
  } = useHoveredStackedAreaField(
    chartRef,
    chartData,
    chartFields,
    usesStackedAreaInteractions,
    totalTooltipField,
    clipHoverLineToArea,
    echartsTheme.todayColor,
  );

  // Chains onto the stacked-area handlers above: both need the same capture-phase mousemove.
  const {
    getCursorDataY,
    onMouseMoveCapture: handleMouseMoveCapture,
    onMouseLeave: handleMouseLeave,
  } = useChartCursorDataY({
    chartRef,
    finder: CURSOR_FINDER,
    enabled: !!nearestSeriesTooltip,
    onMouseMoveCapture,
    onMouseLeave: onChartMouseLeave,
  });

  const option = useMemo(() => {
    if (!renderData?.length || !chartFields?.length) return null;
    const config: LineChartConfig = {
      theme: echartsTheme,
      formatAmount: formatAmount as LineChartConfig['formatAmount'],
      privacyMask: privacyMask as LineChartConfig['privacyMask'],
      stacked,
      withLegend,
      curvy,
      hideAxes,
      colorOverrides,
      areaOpacity,
      lineOpacity,
      lineWidth,
      getCursorDataY: nearestSeriesTooltip ? getCursorDataY : undefined,
      largeToSmall,
      useNumberPrefix,
      hideCents,
      yAxisMin,
      yAxisExtents,
      totalField: totalTooltipField,
      showTooltipTotal,
      tooltipTotalLabel,
      tooltipFields,
      seriesNames,
      tooltipSeriesLabel,
      hoveredFieldNameRef,
      legendHoveredFieldNameRef: legendHoveredFieldRef,
      dateRange,
      preferMonthlyDateLabels,
      denseDateLabels,
      timestep: timestep as Timestep | undefined,
      percent,
      percentMaxMagnitude,
      zeroBaselineLine,
      clipHoverLineToArea,
      stackedAreaLegendTooltipFields,
    };
    return buildLineChartOption(renderData as typeof chartData, chartFields, config);
  }, [
    renderData,
    chartFields,
    echartsTheme,
    formatAmount,
    privacyMask,
    stacked,
    withLegend,
    curvy,
    hideAxes,
    colorOverrides,
    areaOpacity,
    lineOpacity,
    lineWidth,
    nearestSeriesTooltip,
    getCursorDataY,
    largeToSmall,
    useNumberPrefix,
    hideCents,
    yAxisMin,
    yAxisExtents,
    totalTooltipField,
    showTooltipTotal,
    tooltipTotalLabel,
    tooltipFields,
    seriesNames,
    tooltipSeriesLabel,
    hoveredFieldNameRef,
    legendHoveredFieldRef,
    dateRange,
    preferMonthlyDateLabels,
    denseDateLabels,
    timestep,
    percent,
    percentMaxMagnitude,
    zeroBaselineLine,
    clipHoverLineToArea,
    stackedAreaLegendTooltipFields,
  ]);

  const fieldColors = useMemo(() => {
    const colors = colorOverrides ?? echartsTheme.colors;
    const numColors = colors.length;
    const { length } = chartFields;
    return new Map(
      chartFields.map((field, i) => {
        const colorIdx = largeToSmall ? (length - 1 - i) % numColors : i % numColors;
        return [field, colors[colorIdx]];
      }),
    );
  }, [chartFields, colorOverrides, echartsTheme.colors, largeToSmall]);

  const legendRef = useRef<HTMLDivElement>(null);
  const [legendExpanded, setLegendExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);

  useEffect(() => {
    setLegendExpanded(false);
  }, [chartFields]);

  useEffect(() => {
    const { current: el } = legendRef;
    if (!el || legendExpanded) return;
    const checkOverflow = () => setHasOverflow(el.scrollHeight > el.clientHeight);
    checkOverflow();
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(el);
    return () => observer.disconnect();
  }, [legendExpanded, chartFields]);

  if (!option) return null;

  let finalOption: EChartsOption;
  if (typeof optionOverrides === 'function') {
    finalOption = optionOverrides(option as EChartsOption);
  } else if (optionOverrides) {
    finalOption = { ...option, ...optionOverrides } as EChartsOption;
  } else {
    finalOption = option as EChartsOption;
  }

  const legendFields = largeToSmall ? [...chartFields].reverse() : chartFields;

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', width: '100%', ...style }}
      onMouseMoveCapture={handleMouseMoveCapture}
      onMouseLeave={handleMouseLeave}
    >
      <EChartsBase ref={chartRef} option={finalOption} height={height} renderer='canvas' notMerge />
      {useCustomLegend && chartFields.length > 0 && (
        <>
          <div
            ref={legendRef}
            className={classes.legendContainer}
            style={!legendExpanded ? { maxHeight: LEGEND_COLLAPSED_HEIGHT, overflow: 'hidden' } : undefined}
          >
            {legendFields.map((field) => (
              <div
                key={field}
                className={classes.legendItem}
                onMouseEnter={() => {
                  chartRef.current?.dispatchAction({ type: 'highlight', seriesName: field });
                  showTooltipForField(field);
                }}
                onMouseLeave={() => {
                  chartRef.current?.dispatchAction({ type: 'downplay', seriesName: field });
                  hideTooltip();
                }}
              >
                <div className={classes.legendMarker} style={{ backgroundColor: fieldColors.get(field) }} />
                <span className={classes.legendLabel}>{seriesNames?.[field] ?? field}</span>
              </div>
            ))}
          </div>
          {hasOverflow && (
            <div className={classes.legendToggle}>
              <QButton
                variant='tonal'
                size='small'
                endIcon={legendExpanded ? <KeyboardArrowUpRoundedIcon /> : <KeyboardArrowDownRoundedIcon />}
                onClick={() => setLegendExpanded((prev: boolean) => !prev)}
              >
                {legendExpanded ? 'Show Less' : 'Show All'}
              </QButton>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const MultilineChart = forwardRef<MultilineChartRef, MultilineChartProps>(MultilineChartImpl);

export default MultilineChart;
